import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'

import { AboSettingsApi, AboTopicsApi, MediaFileApi, PageSettingsApi } from '../../api/instances'
import { Severity, EventType } from '@professocial/professocialTypes/types/messageTypes'
import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'

import {
  acknowledgeSettingsForNetworkPage,
  acknowledgeSettingsForNetworkPageInitiated,
  createAboTopic,
  createAboTopicInitiated,
  deleteAboTopic,
  deleteAboTopicInitiated,
  getAboSettingsForCurrentUser,
  getAboSettingsForCurrentUserInitiated,
  getAboTopicHasPostings,
  getAboTopicHasPostingsInitiated, getAuditLogForNetworkPage,
  getAuditLogForNetworkPageInitiated,
  getTopicsAndMetadataForCurrentUser,
  getTopicsAndMetadataForCurrentUserInitiated,
  updateAboSettingsForCurrentUser,
  updateAboSettingsForCurrentUserInitiated,
  updateAboTopic,
  updateAboTopicInitiated,
  updateCorePageSettings,
  updateCorePageSettingsInitiated,
  uploadTemporaryMediaFile,
  uploadTemporaryMediaFileInitiated,
} from './topic.actions'
import {
  CreateAboTopicRequest,
  DeleteAboTopicRequest, GetAboTopicHasPostingsRequest,
  UpdateAboTopicRequest,
} from '@3m5/sm_publish-frontend-api/apis/AboTopicsApi'
import {
  AcknowledgeSettingsForNetworkPageRequest,
  GetAuditLogForNetworkPageRequest,
  UpdateAboSettingsForCurrentUserRequest,
} from '@3m5/sm_publish-frontend-api/apis/AboSettingsApi'
import { UpdateCorePageSettingsRequest, UploadTemporaryMediaFileRequest } from '@3m5/sm_publish-frontend-api'
import i18n from 'i18next'

// createAboTopic
const createAboTopicWorker = bindAsyncAction(createAboTopic)(
  function * (params: CreateAboTopicRequest) {
    return yield call([AboTopicsApi, AboTopicsApi.createAboTopic], params)
  },
)

function * watchCreateAboTopicInitiated () {
  while (true) {
    const action: Action<CreateAboTopicRequest> = yield take(createAboTopicInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield createAboTopicWorker(action.payload)
      yield put(setMessage({
        severity: Severity.success,
        message: i18n.t('topicsMessages:isSuccessfullyCreated', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.create,
      }))
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:createError', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.create,
      }))
      yield put(requestFailed({ type: action.type, error: e }))
      console.error('error in watchCreateAboTopicInitiated: ', e)
    }
  }
}

// deleteAboTopic
const deleteAboTopicWorker = bindAsyncAction(deleteAboTopic)(
  function * (params: DeleteAboTopicRequest) {
    return yield call([AboTopicsApi, AboTopicsApi.deleteAboTopic], params)
  },
)

function * watchDeleteAboTopicInitiated () {
  while (true) {
    const action: Action<DeleteAboTopicRequest> = yield take(deleteAboTopicInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield deleteAboTopicWorker(action.payload)
      yield put(setMessage({
        severity: Severity.success,
        message: i18n.t('topicsMessages:isSuccessfullyDeleted', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.delete,
      }))
      yield put(requestFinished({ type: action.type }))
    } catch (e: any) {
      // if 500 - show dialog component in ProfessocialTopics
      yield e.status !== 500 && put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:deleteError', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.delete,
      }))
      yield put(requestFailed({ type: action.type, error: e }))
      console.error('error in watchDeleteAboTopicInitiated: ', e)
    }
  }
}

// getTopicsAndMetadataForCurrentUser
const getTopicsAndMetadataForCurrentUserWorker = bindAsyncAction(getTopicsAndMetadataForCurrentUser)(
  function * () {
    return yield call([AboTopicsApi, AboTopicsApi.getTopicsAndMetadataForCurrentUser])
  },
)

function * watchGetTopicsAndMetadataForCurrentUserInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getTopicsAndMetadataForCurrentUserInitiated)
    try {
      yield getTopicsAndMetadataForCurrentUserWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:loadingError', { context: i18n.t('topicsCommon:topicsAndMetadataForCurrentUser') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetTopicsAndMetadataForCurrentUserInitiated: ', e)
    }
  }
}

// updateAboTopic
const updateAboTopicWorker = bindAsyncAction(updateAboTopic)(
  function * (params: UpdateAboTopicRequest) {
    return yield call([AboTopicsApi, AboTopicsApi.updateAboTopic], params)
  },
)

function * watchUpdateAboTopicInitiated () {
  while (true) {
    const action: Action<UpdateAboTopicRequest> = yield take(updateAboTopicInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updateAboTopicWorker(action.payload)
      yield put(setMessage({
        severity: Severity.success,
        message: i18n.t('topicsMessages:isSuccessfullyUpdated', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.update,
      }))
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:updateError', { context: i18n.t('topicsCommon:topic') }),
        eventType: EventType.update,
      }))
      yield put(requestFailed({ type: action.type, error: e }))
      console.error('error in watchUpdateAboTopicInitiated: ', e)
    }
  }
}

// getAboSettingsForCurrentUser
const getAboSettingsForCurrentUserWorker = bindAsyncAction(getAboSettingsForCurrentUser)(
  function * () {
    return yield call([AboSettingsApi, AboSettingsApi.getAboSettingsForCurrentUser])
  },
)

function * watchGetAboSettingsForCurrentUserInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getAboSettingsForCurrentUserInitiated)
    try {
      yield getAboSettingsForCurrentUserWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:loadingError', { context: i18n.t('topicsCommon:aboSettingsForCurrentUser') }),
        eventType: EventType.load,
      }))
      console.error('error in watchGetAboSettingsForCurrentUserInitiated: ', e)
    }
  }
}

// updateAboSettingsForCurrentUser
const updateAboSettingsForCurrentUserWorker = bindAsyncAction(updateAboSettingsForCurrentUser)(
  function * (params: UpdateAboSettingsForCurrentUserRequest) {
    return yield call([AboSettingsApi, AboSettingsApi.updateAboSettingsForCurrentUser], params)
  },
)

function * watchUpdateAboSettingsForCurrentUserInitiated () {
  while (true) {
    const action: Action<UpdateAboSettingsForCurrentUserRequest> = yield take(updateAboSettingsForCurrentUserInitiated)
    try {
      yield updateAboSettingsForCurrentUserWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:updatingError', { context: i18n.t('topicsCommon:aboSettingsForCurrentUser') }),
        eventType: EventType.update,
      }))
      console.error('error in watchUpdateAboSettingsForCurrentUserInitiated: ', e)
    }
  }
}

// uploadTemporaryMediaFile
const uploadTemporaryMediaFileWorker = bindAsyncAction(uploadTemporaryMediaFile)(
  function * (params: UploadTemporaryMediaFileRequest) {
    return yield call([MediaFileApi, MediaFileApi.uploadTemporaryMediaFile], params)
  },
)

function * watchUploadTemporaryMediaFileInitiated () {
  while (true) {
    const action: Action<UploadTemporaryMediaFileRequest> = yield take(uploadTemporaryMediaFileInitiated)
    try {
      yield uploadTemporaryMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:loadError', { context: i18n.t('topicsCommon:temporaryMediaFile') }),
        eventType: EventType.upload,
      }))
      console.error('error in watchUploadTemporaryMediaFileInitiated: ', e)
    }
  }
}

// updateCorePageSettings
const updateCorePageSettingsWorker = bindAsyncAction(updateCorePageSettings)(
  function * (params: UpdateCorePageSettingsRequest) {
    return yield call([PageSettingsApi, PageSettingsApi.updateCorePageSettings], params)
  },
)

function * UpdateCorePageSettingsInitiated () {
  while (true) {
    const action: Action<UpdateCorePageSettingsRequest> = yield take(updateCorePageSettingsInitiated)
    try {
      yield updateCorePageSettingsWorker(action.payload)
      yield put(setMessage({
        severity: Severity.success,
        message: i18n.t('topicsMessages:isSuccessfullyUpdated', { context: i18n.t('topicsCommon:aboEmailShort') }),
        eventType: EventType.update,
      }))
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: i18n.t('topicsMessages:updateError', { context: i18n.t('topicsCommon:aboEmailShort') }),
        eventType: EventType.update,
      }))
    }
  }
}

// getAboTopicHasPostings
const getAboTopicHasPostingsWorker = bindAsyncAction(getAboTopicHasPostings)(
  function * (params: GetAboTopicHasPostingsRequest) {
    return yield call([AboTopicsApi, AboTopicsApi.getAboTopicHasPostings], params)
  },
)

function * watchGetAboTopicHasPostingsInitiated () {
  while (true) {
    const action: Action<GetAboTopicHasPostingsRequest> = yield take(getAboTopicHasPostingsInitiated)
    try {
      yield getAboTopicHasPostingsWorker(action.payload)
    } catch (e) {
      console.log(e)
    }
  }
}

// aacknowledgeSettingsForNetworkPage
const acknowledgeSettingsForNetworkPageWorker = bindAsyncAction(acknowledgeSettingsForNetworkPage)(
  function * (params: AcknowledgeSettingsForNetworkPageRequest) {
    return yield call([AboSettingsApi, AboSettingsApi.acknowledgeSettingsForNetworkPage], params)
  },
)

function * watchAcknowledgeSettingsForNetworkPageInitiated () {
  while (true) {
    const action: Action<AcknowledgeSettingsForNetworkPageRequest> = yield take(acknowledgeSettingsForNetworkPageInitiated)
    try {
      yield acknowledgeSettingsForNetworkPageWorker(action.payload)
    } catch (e) {
      console.log(e)
    }
  }
}

// getAuditLogForNetworkPage
const getAuditLogForNetworkPageWorker = bindAsyncAction(getAuditLogForNetworkPage)(
  function * (params: GetAuditLogForNetworkPageRequest) {
    return yield call([AboSettingsApi, AboSettingsApi.getAuditLogForNetworkPage], params)
  },
)

function * watchGetAuditLogForNetworkPageInitiated () {
  while (true) {
    const action: Action<GetAuditLogForNetworkPageRequest> = yield take(getAuditLogForNetworkPageInitiated)
    try {
      yield getAuditLogForNetworkPageWorker(action.payload)
    } catch (e) {
      console.log(e)
    }
  }
}

export function * topicSagas () {
  yield fork(watchCreateAboTopicInitiated)
  yield fork(watchDeleteAboTopicInitiated)
  yield fork(watchGetTopicsAndMetadataForCurrentUserInitiated)
  yield fork(watchUpdateAboTopicInitiated)
  yield fork(watchGetAboSettingsForCurrentUserInitiated)
  yield fork(watchUpdateAboSettingsForCurrentUserInitiated)
  yield fork(watchUploadTemporaryMediaFileInitiated)
  yield fork(watchGetAboTopicHasPostingsInitiated)
  yield fork(watchAcknowledgeSettingsForNetworkPageInitiated)
  yield fork(watchGetAuditLogForNetworkPageInitiated)
  yield fork(UpdateCorePageSettingsInitiated)
}
